import React, { useState, useEffect } from 'react';
import { createUser, signInUser } from '../firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import './AuthModal.css';

const AuthModal = ({ onClose, onAuthChange }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [validations, setValidations] = useState({
    email: { valid: false, message: '' },
    password: { valid: false, message: '' },
    phoneNumber: { valid: false, message: '' }
  });
  const [isForgotPassword, setIsForgotPassword] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);
    setValidations(prev => ({
      ...prev,
      email: {
        valid: isValid,
        message: isValid ? '' : 'Email không hợp lệ'
      }
    }));
    return isValid;
  };

  const validatePassword = (password) => {
    const isValid = password.length >= 6;
    setValidations(prev => ({
      ...prev,
      password: {
        valid: isValid,
        message: isValid ? '' : 'Mật khẩu phải có ít nhất 6 ký tự'
      }
    }));
    return isValid;
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[0-9]{10}$/;
    const isValid = phoneRegex.test(phone);
    setValidations(prev => ({
      ...prev,
      phoneNumber: {
        valid: isValid,
        message: isValid ? '' : 'Số điện thoại phải có 10 chữ số'
      }
    }));
    return isValid;
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    validateEmail(value);
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    validatePassword(value);
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhoneNumber(value);
    validatePhoneNumber(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (isSignUp) {
        // Validate all fields for sign up
        const isEmailValid = validateEmail(email);
        const isPasswordValid = validatePassword(password);
        const isPhoneValid = validatePhoneNumber(phoneNumber);

        if (!isEmailValid || !isPasswordValid || !isPhoneValid) {
          setError('Vui lòng kiểm tra lại thông tin đăng ký');
          setLoading(false);
          return;
        }

        await createUser(email, password, displayName, phoneNumber);
        setSuccess('Đăng ký thành công!');
      } else {
        // Validate required fields for sign in
        const isEmailValid = validateEmail(email);
        const isPasswordValid = validatePassword(password);

        if (!isEmailValid || !isPasswordValid) {
          setError('Vui lòng kiểm tra lại thông tin đăng nhập');
          setLoading(false);
          return;
        }

        await signInUser(email, password);
        setSuccess('Đăng nhập thành công!');
      }

      onAuthChange();
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      let errorMessage = 'Đã có lỗi xảy ra. Vui lòng thử lại.';
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'Email này đã được sử dụng.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Email không hợp lệ.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Mật khẩu quá yếu.';
      } else if (err.code === 'auth/user-not-found') {
        errorMessage = 'Không tìm thấy tài khoản với email này.';
      } else if (err.code === 'auth/wrong-password') {
        errorMessage = 'Sai mật khẩu.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const isEmailValid = validateEmail(email);
      if (!isEmailValid) {
        setError('Vui lòng kiểm tra lại email');
        setLoading(false);
        return;
      }

      await sendPasswordResetEmail(auth, email);
      setSuccess('Link đặt lại mật khẩu đã được gửi đến email của bạn');
      setTimeout(() => {
        setIsForgotPassword(false);
      }, 3000);
    } catch (err) {
      let errorMessage = 'Đã có lỗi xảy ra. Vui lòng thử lại.';
      if (err.code === 'auth/user-not-found') {
        errorMessage = 'Không tìm thấy tài khoản với email này.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="auth-modal">
        <div className="auth-modal-content">
          <div className="auth-logo">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 700 150" style={{ width: '280px', height: 'auto' }}>
              <g transform="translate(20, 20)">
                <text x="0" y="85" 
                      fontFamily="'Montserrat', sans-serif" 
                      fontWeight="700" 
                      fontSize="72" 
                      letterSpacing="-1"
                      fill="#333333">Luận</text>
                <rect x="185" y="10" 
                      width="100" height="100" 
                      rx="20" 
                      fill="none"
                      stroke="#333333"
                      strokeWidth="4"/>
                <text x="235" y="85" 
                      fontFamily="'Helvetica Neue', Arial, sans-serif" 
                      fontWeight="900" 
                      fontSize="72"
                      letterSpacing="-1"
                      textAnchor="middle"
                      fill="#6f01ff">Số</text>
                <text x="310" y="85" 
                      fontFamily="'Montserrat', sans-serif" 
                      fontWeight="700" 
                      fontSize="72" 
                      letterSpacing="-1"
                      fill="#333333">Điện Thoại</text>
              </g>
            </svg>
          </div>
          <button className="auth-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
          
          <h2>{isForgotPassword ? 'Quên mật khẩu' : (isSignUp ? 'Đăng ký' : 'Đăng nhập')}</h2>
          
          {isForgotPassword ? (
            <form onSubmit={handleForgotPassword}>
              <div className="auth-input-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Nhập địa chỉ email"
                  required
                  className={validations.email.message ? 'error' : ''}
                />
                {validations.email.message && (
                  <div className="auth-error">{validations.email.message}</div>
                )}
              </div>

              {error && <div className="auth-error">{error}</div>}
              {success && <div className="auth-success">{success}</div>}

              <button type="submit" disabled={loading} className="auth-button">
                {loading ? (
                  <>
                    Đang gửi...
                    <div className="loading-spinner"></div>
                  </>
                ) : (
                  'Gửi link đặt lại mật khẩu'
                )}
              </button>

              <div className="auth-toggle">
                <button type="button" onClick={() => setIsForgotPassword(false)}>
                  Quay lại đăng nhập
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmit}>
              {isSignUp && (
                <div className="auth-input-group">
                  <label htmlFor="displayName">Tên hiển thị</label>
                  <input
                    type="text"
                    id="displayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Nhập tên hiển thị"
                    required
                  />
                </div>
              )}

              {isSignUp && (
                <div className="auth-input-group">
                  <label htmlFor="phoneNumber">Số điện thoại</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    placeholder="Nhập số điện thoại"
                    required
                    className={validations.phoneNumber.message ? 'error' : ''}
                  />
                  {validations.phoneNumber.message && (
                    <div className="auth-error">{validations.phoneNumber.message}</div>
                  )}
                </div>
              )}

              <div className="auth-input-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Nhập địa chỉ email"
                  required
                  className={validations.email.message ? 'error' : ''}
                />
                {validations.email.message && (
                  <div className="auth-error">{validations.email.message}</div>
                )}
              </div>

              <div className="auth-input-group">
                <label htmlFor="password">Mật khẩu</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Nhập mật khẩu"
                  required
                  className={validations.password.message ? 'error' : ''}
                />
                {validations.password.message && (
                  <div className="auth-error">{validations.password.message}</div>
                )}
              </div>

              {!isSignUp && (
                <div className="forgot-password">
                  <button 
                    type="button" 
                    onClick={() => setIsForgotPassword(true)}
                    className="forgot-password-link"
                  >
                    Quên mật khẩu?
                  </button>
                </div>
              )}

              {error && <div className="auth-error">{error}</div>}
              {success && <div className="auth-success">{success}</div>}

              <button type="submit" disabled={loading} className="auth-button">
                {loading ? (
                  <>
                    {isSignUp ? 'Đang đăng ký...' : 'Đang đăng nhập...'}
                    <div className="loading-spinner"></div>
                  </>
                ) : (
                  isSignUp ? 'Đăng ký' : 'Đăng nhập'
                )}
              </button>
            </form>
          )}

          <div className="auth-divider">
            <span>hoặc</span>
          </div>

          <div className="auth-toggle">
            {isSignUp ? (
              <>
                Đã có tài khoản?{' '}
                <button type="button" onClick={() => setIsSignUp(false)}>
                  Đăng nhập
                </button>
              </>
            ) : (
              <>
                Chưa có tài khoản?{' '}
                <button type="button" onClick={() => setIsSignUp(true)}>
                  Đăng ký
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
